import axios from "axios";
import { auth, api as salesiveApi } from "salesive-api-axios";
import { setSessionIdentity } from "../components/chat/chatUtils.js";
import { applySessionMirror } from "./sessionState.js";

const isLogoutResponse = (response) => {
    const url = response?.config?.url || "";
    return /\/auth\/logout(\?|$)/.test(url);
};

const roleFromResponse = (response) => {
    const data = response?.data;
    if (!data || typeof data !== "object") return null;
    const inner = data.data && typeof data.data === "object" ? data.data : data;
    return inner.role || inner.user?.role || null;
};

// Routes whose successful response carries the authenticated user record.
// Used to populate the in-memory session identity cache so synchronous
// consumers (e.g. message attribution) can resolve the current user without
// having to read an httpOnly cookie.
const SESSION_IDENTITY_PATHS = [
    "/auth/verify",
    "/auth/authenticate",
    "/auth/token-login",
    "/auth/me",
    "/auth/session",
    "/auth/ghost",
    "/user",
];

const extractIdentityFromResponse = (response) => {
    if (!response) return null;
    const data = response.data;
    if (!data || typeof data !== "object") return null;
    const inner = data.data && typeof data.data === "object" ? data.data : {};
    const user =
        inner.user && typeof inner.user === "object" ? inner.user : {};
    const merged = { ...data, ...inner, ...user };
    const id =
        merged.id || merged._id || merged.userId || merged.customerId || null;
    const email = merged.email || null;
    if (!id && !email) return null;
    return { id, email };
};

const isSessionIdentityRoute = (response) => {
    const url = response?.config?.url || "";
    const method = String(response?.config?.method || "get").toLowerCase();
    // Only POSTs that complete an auth step, plus the GET profile endpoints.
    const isAuthyGet =
        method === "get" &&
        SESSION_IDENTITY_PATHS.some(
            (p) => url === p || url.endsWith(p),
        );
    const isAuthyPost =
        method === "post" &&
        SESSION_IDENTITY_PATHS.some(
            (p) => url === p || url.endsWith(p),
        );
    return isAuthyGet || isAuthyPost;
};

/**
 * Axios instance with base configuration
 * Exposed on window.SalesiveAPI for external access
 */
const api = axios.create({
    baseURL:
        import.meta.env.VITE_API_URL || "https://events.salesive.com/api/v1",
    timeout: 30000,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
        "x-shop-id":
            import.meta.env.VITE_SALESIVE_SHOP_ID ||
            localStorage.getItem("SALESIVE_SHOP_ID"),
    },
});

/**
 * Clamp helper for message history limits
 */
function clampLimit(limit = 50) {
    const value = Number(limit) || 50;
    return Math.min(Math.max(value, 1), 100);
}

/**
 * Fetch recent customer messages via REST API
 * @param {object} options
 * @param {number} [options.limit=50] - number of messages (max 100)
 * @param {string} [options.shopId] - optional override for shop header
 */
export async function fetchCustomerMessages(options = {}) {
    const { limit = 50 } = options;

    const response = await api.get("/messages/me", {
        params: { limit: clampLimit(limit) },
    });

    return response.data;
}

export async function fetchConversations(options = {}) {
    // Storefront chat only, always. This popup is the web-chat surface, but
    // the endpoint it reads is shared with the merchant inbox, which lists
    // WhatsApp, Instagram and Telegram threads too. Those are also typed
    // shop_support, so without this filter a merchant opening their own
    // storefront (a shop member sees every shop conversation), or a shopper
    // whose phone matched a WhatsApp sender (linked as a participant), got a
    // WhatsApp thread rendered here as if it were web chat. "chat" is the
    // server's name for "no platform", which is what storefront threads are.
    const { shopId, type, limit = 20, platform = "chat" } = options;

    const response = await api.get("/messages/conversations", {
        params: {
            ...(shopId ? { shopId } : {}),
            ...(type ? { type } : {}),
            platform,
            limit: Number(limit) || 20,
        },
    });

    return response.data;
}

export async function createConversation(options = {}) {
    const { type = "shop_support" } = options;

    const response = await api.post("/messages/conversations", {
        type,
    });

    return response.data;
}

export async function uploadImage(file) {
    const formData = new FormData();
    formData.append("image", file);

    const response = await salesiveApi.post("/image/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
    });

    return response.data;
}

export async function fetchConversationMessages(conversationId, options = {}) {
    const { limit = 50 } = options;

    if (!conversationId) {
        throw new Error("conversationId is required");
    }

    const response = await api.get(`/messages/${conversationId}`, {
        params: { limit: clampLimit(limit) },
    });

    return response.data;
}

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Always send cookies so the parent-domain `jwt` cookie set by the
        // store API (store.salesive.com) is attached to events API requests
        // (events.salesive.com) — this is how SSO between subdomains works.
        config.withCredentials = true;

        // Fallback: if we still have a token in localStorage, send it as a
        // Bearer header too. The events auth middleware currently reads the
        // header (not the cookie), so this keeps it working until the backend
        // is updated to read the cookie. Once that's done this branch can be
        // deleted and the cookie will be the single source of truth.
        const token = localStorage.getItem("SALESIVE_ACCESS_TOKEN");
        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        // Populate the in-memory session identity cache from auth responses
        // so synchronous consumers (message attribution, etc.) can resolve
        // the current user without having to read an httpOnly cookie.
        if (isSessionIdentityRoute(response)) {
            try {
                const identity = extractIdentityFromResponse(response);
                setSessionIdentity(identity);
            } catch {}
            // Track auth state for hasSession()/isLoggedIn() callers. Prefer the
            // role field when present so /auth/session and /auth/ghost don't get
            // mis-labeled as a signed-in user.
            if (isLogoutResponse(response)) {
                applySessionMirror({ active: false, user: false });
            } else {
                const role = roleFromResponse(response);
                if (role) {
                    applySessionMirror({
                        active: true,
                        user: role !== "ghost",
                    });
                }
            }
        }
        return response;
    },
    (error) => {
        // Handle common errors
        if (error.response?.status === 401) {
            console.warn("[SalesiveAPI] Unauthorized request");
            // Clear any local token so the next request doesn't keep retrying
            // with a stale Authorization header. The server's cookie is
            // cleared via /auth/logout on the store API, but we don't want
            // to block the recovery on that round-trip.
            applySessionMirror({ active: false, user: false });
            setSessionIdentity(null);
            auth.logout();
            auth.createGhost().catch(() => {
                // If ghost creation also fails, swallow — the user will
                // simply see the next request fail and can re-auth.
            });
        }
        return Promise.reject(error);
    },
);

// Attach helpers to the axios instance
api.fetchCustomerMessages = fetchCustomerMessages;
api.fetchConversations = fetchConversations;
api.createConversation = createConversation;
api.fetchConversationMessages = fetchConversationMessages;
api.uploadImage = uploadImage;

// Expose on window
if (typeof window !== "undefined") {
    window.SalesiveAPI = api;
}

export default api;
